// @flow

import React, { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";
import actionCreators from "@modules/service/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import type { State } from "@store/reducers";
import type {
  StandardQuestion,
  StandardQuestionContent,
} from "@modules/service/reducer/standard-questions/types";
import type { HierarchyAssignment } from "@modules/service/types";
import * as serviceSelectors from "@modules/service/selectors";
import Step1MainDetails from "./step1-main-details";
import Step2HierarchyScope from "./step2-hierarchy-scope";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  NonLinearWizard,
  Step,
  TooltipDropdown,
} from "@components";
import { getSchema } from "@utils/schema";
const schema = getSchema("StandardQuestionRevisionDTO");

type PropsFromState = $Exact<{
  standardQuestionContainer: ?StandardQuestion,
  draft: ?StandardQuestionContent,
  assignments: Array<HierarchyAssignment>,
  isAssignmentsValid: boolean,
}>;

type PropsFromDispatch = $Exact<{
  setShowAddStandardQuestionModal: typeof actionCreators.SetShowAddStandardQuestionModal,
  addNewStandardQuestion: typeof actionCreators.AddNewStandardQuestion,
  setStandardQuestionRevisionSchema: typeof actionCreators.SetStandardQuestionRevisionSchema,
  getHierarchyTree: typeof sharedActionCreators.GetHierarchyTree,
}>;

type OwnProps = $Exact<{
  clientId: number,
  defaultStep: number,
  filter: string,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class AddStandardQuestionWizardModal extends Component<Props> {
  componentDidMount() {
    this.props.getHierarchyTree({
      clientId: this.props.clientId,
    });

    if (this.props.setStandardQuestionRevisionSchema) {
      this.props.setStandardQuestionRevisionSchema(schema);
    }
  }
  handleCancel = () => {
    this.props.setShowAddStandardQuestionModal({ show: false, step: 1 });
  };

  handleSubmitStandardQuestionDraft = () => {
    if (this.props.draft) {
      this.props.addNewStandardQuestion({
        clientId: this.props.clientId,
        draft: this.props.draft,
        assignments: this.props.assignments,
        filter: this.props.filter,
      });
    }
  };

  render() {
    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
          {this.props.t("ADD_STANDARD_QUESTION_MODAL_TITLE")}
        </ModalHeader>
        <ModalBody className="d-flex flex-column align-content-center">
          <NonLinearWizard defaultStep={this.props.defaultStep} data={[]}>
            <Step component={Step1MainDetails} title={this.props.t("MAIN_DETAILS")} />
            <Step
              component={Step2HierarchyScope}
              title={
                <span>
                  <TooltipDropdown
                    description={this.props.t("ADD_STANDARD_QUESTION_MODAL_STEP_2_TITLE_TOOLTIP")}
                    id={"add-standard-question-modal-step-2-title"}
                  >
                    {this.props.t("HIERARCHY_SCOPE")}
                  </TooltipDropdown>
                </span>
              }
            />
          </NonLinearWizard>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.handleCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button
            materialIcon="save"
            onClick={this.handleSubmitStandardQuestionDraft}
            disabled={!this.props.isAssignmentsValid}
          >
            {this.props.t("SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    standardQuestionContainer: serviceSelectors.getStandardQuestionContainer(state),
    draft: serviceSelectors.getStandardQuestionDraft(state),
    assignments: serviceSelectors.getStandardQuestionAssignments(state),
    isAssignmentsValid: serviceSelectors.isStandardQuestionAssignmentsValid(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setShowAddStandardQuestionModal: actionCreators.SetShowAddStandardQuestionModal,
  addNewStandardQuestion: actionCreators.AddNewStandardQuestion,
  setStandardQuestionRevisionSchema: actionCreators.SetStandardQuestionRevisionSchema,
  getHierarchyTree: sharedActionCreators.GetHierarchyTree,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AddStandardQuestionWizardModal);
